import { CmcWebApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { makeConfirmationsParamKey } from './keys';
import {
  AccountKey,
  AccountType,
  CmcWebApiAccountHoldingLeanModel1,
  IAccount,
  IAccountHoldings,
  IAccountOptionHoldings,
  IAccountProfitLoss,
  IConfirmations,
  MarginLendingProvider,
  Sort
} from './types';

export const ACCOUNT_STORE_NAME = `accounts`;

export type IAccountStore = {
  account: Record<AccountKey, IAccount>;
  profitLoss: Record<AccountKey, IAccountProfitLoss>;
  holdings: Record<AccountKey, IAccountHoldings>;
  optionHoldings: Record<AccountKey, IAccountOptionHoldings>;
  confirmations: Record<AccountKey, IConfirmations>;
  hash?: number;
  selected?: AccountKey;
};

export const defaultAccountStore: IAccountStore = {
  account: {},
  profitLoss: {},
  holdings: {},
  optionHoldings: {},
  confirmations: {}
};

const defaultProfitLoss: IAccountProfitLoss = {
  items: [],
  pages: 0,
  hash: 0,
  hin: '',
  fiatSettlementCurrency: '',
  fiatSettlementCurrencySymbol: ''
};

export const createAccountStore = (initialState: IAccountStore) =>
  createSlice({
    name: ACCOUNT_STORE_NAME,
    initialState,
    reducers: {
      storeReset: () => defaultAccountStore,

      leanAccountsFetched: (store, action: PayloadAction<CmcWebApi.Account.TradingAccountModel2[]>) => {
        action.payload
          .filter((account) => !store.account[account.AccountNumber])
          .forEach((account) => {
            if (account.IsSelected) {
              store.selected = account.AccountNumber;
            }

            store.account[account.AccountNumber] = {
              isLean: true,
              name: account.AccountName,
              nickName: account.AccountNickName,
              number: account.AccountNumber,
              selected: account.IsSelected
            };
          });
      },

      accountsFetched: (store, action: PayloadAction<{ accounts: CmcWebApi.Account.TradingAccountModel2[]; selected: string; hash: number }>) => {
        if (store.hash !== action.payload.hash) {
          store.hash = action.payload.hash;
        }

        if (action.payload.selected) {
          store.selected = action.payload.selected;
        }

        if (action.payload.accounts) {
          action.payload.accounts.forEach((account) => {
            store.account[account.AccountNumber] = {
              isLean: false,
              name: account.AccountName,
              nickName: account.AccountNickName,
              number: account.AccountNumber,
              selected: account.IsSelected,
              fiatSettlementCurrency: account.FiatSettlementCurrency,
              businessRegionId: account.BusinessRegionId,
              cashAccount: {
                bsb: account.CashAccountBsb,
                no: account.CashAccountNumber,
                instCode: account.CashAccountInstCode,
                canView: account.CanViewCashAccount
              },
              chessAccountNumber: account.ChessAccountNumber,
              tier: account.Tier,
              directDebitLimit: account.DirectDebitLimit,
              isExternalCash: account.IsExternalCash,
              displayNonVettedCash: account.DisplayNonVettedCash,
              isExternalHoldings: account.IsExternalHoldings,
              displayNonVettedHoldings: account.DisplayNonVettedHoldings,
              isJointAccount: account.IsJointAccount,
              isCompanyAccount: account.IsCompanyAccount,
              type: account.AccountType as AccountType,
              data: {
                bankBalance: account.AccountData.BankBalance,
                availableFunds: account.AccountData.AvailableFunds,
                openBuyOrders: account.AccountData.OpenBuyOrders,
                openBuyOrdersCount: account.AccountData.OpenBuyOrdersCount,
                sellOrdersCount: account.AccountData.SellOrdersCount,
                unsettledAmounts: account.AccountData.UnsettledAmounts,
                tradesToday: account.AccountData.TradesToday,
                tradesTodayCount: account.AccountData.TradesTodayCount,
                withdrawalAmount: account.AccountData.WithdrawalAmount,
                stockHoldings: account.AccountData.StockHoldings,
                optionsMarginAmount: account.AccountData.OptionsMarginAmount
              },
              hin: account.Hin,
              // otherClients: IOtherClients[];
              optionsAccountNumber: account.OptionsAccountNumber,
              canTrade: account.CanTrade,
              isRealTimeBalance: account.IsRealTimeBalance,
              primaryAdviser: {
                code: account.PrimaryAdviserCode,
                name: account.PrimaryAdviserName
              },
              isUSTaxFormReceived: account.IsUSTaxFormReceived,
              refreshGbstBalance: account.RefreshGbstBalance,
              isMarginAccount: account.IsMarginAccount,
              marginLendingProvider: account.MarginLendingProvider as MarginLendingProvider,
              cryptoOrdersEnabled: account.CryptoOrdersEnabled
            };
          });
        }
      },

      accountSelected: (store, action: PayloadAction<AccountKey>) => {
        store.selected = action.payload;
      },

      profitLossHashReset: (store, action: PayloadAction<AccountKey>) => {
        const profitLoss = (store.profitLoss[action.payload] ??= { ...defaultProfitLoss });
        profitLoss.hash = 0;
      },

      profitLossFetched: (
        store,
        action: PayloadAction<{
          accountNo: AccountKey;
          hash: number;
          profitLoss: CmcWebApi.Account.ProfitLossFullModel3;
        }>
      ) => {
        const profitLoss = (store.profitLoss[action.payload.accountNo] ??= { ...defaultProfitLoss });
        profitLoss.hash = action.payload.hash;
        profitLoss.pages = action.payload.profitLoss.TotalPages;
        profitLoss.hin = action.payload.profitLoss.HinNumber;
        profitLoss.fiatSettlementCurrency = action.payload.profitLoss.FiatSettlementCurrency;
        profitLoss.fiatSettlementCurrencySymbol = action.payload.profitLoss.FiatSettlementCurrencySymbol;
        profitLoss.showHinNumber = action.payload.profitLoss.ShowHinNumber;

        profitLoss.items = action.payload.profitLoss.ClientHoldings.map((holding) => ({
          exchangeId: holding.Quote.ExchangeId,
          averagePrice: holding.AveragePrice,
          cost: holding.Cost,
          id: holding.Id,
          isUserAveragePrice: holding.IsUserAveragePrice,
          isUserHoldingQuantity: holding.IsUserHoldingQuantity,
          quantity: holding.HoldingQuantity,
          instrument: holding.StockCode,
          instrumentDisplayCode: holding.InstrumentDisplayCode
        }));
      },

      holdingsFetched: (store, action: PayloadAction<{ accountNo: AccountKey; hash: number; holdings: CmcWebApiAccountHoldingLeanModel1[] }>) => {
        const holdings = (store.holdings[action.payload.accountNo] ??= { hash: 0, items: [] });
        holdings.hash = action.payload.hash;

        holdings.items = action.payload.holdings.map((holding) => ({
          instrument: holding.Inst,
          availableHoldings: holding.Avail,
          chessHoldings: holding.ChH,
          conditionalBuys: holding.CB,
          conditionalSells: holding.CS,
          ochSubposition: holding.Och,
          openBuys: holding.OB,
          openSells: holding.OS,
          recentBuys: holding.RB,
          recentSells: holding.RS
        }));
      },

      optionHoldingsFetched: (store, action: PayloadAction<{ accountNo: AccountKey; optionHoldings: CmcWebApi.Account.AccountOptionPositionsModel1[] }>) => {
        const optionHoldings = (store.optionHoldings[action.payload.accountNo] ??= { items: [] });

        optionHoldings.items = action.payload.optionHoldings;
      },

      confirmationsCountFetched: (store, action: PayloadAction<{ accountNo: AccountKey; from: number; to: number; count: number; hash: number }>) => {
        const confirmations = (store.confirmations[action.payload.accountNo] ??= { from: {} });
        const from = (confirmations.from[action.payload.from] ??= { to: {} });
        const to = (from.to[action.payload.to] ??= { count: 0, hash: 0, params: {} });

        to.count = action.payload.count;
        to.hash = action.payload.hash;
      },

      confirmationsPageFetched: (
        store,
        action: PayloadAction<{
          accountNo: AccountKey;
          from: number;
          to: number;
          pageSize: number;
          sortColumn: string;
          sortDirection: Sort;
          page: number;
          items: CmcWebApi.Account.ConfirmationModel3[];
        }>
      ) => {
        const key = makeConfirmationsParamKey(action.payload.pageSize, action.payload.sortColumn, action.payload.sortDirection);
        const confirmations = (store.confirmations[action.payload.accountNo] ??= { from: {} });
        const from = (confirmations.from[action.payload.from] ??= { to: {} });
        const to = (from.to[action.payload.to] ??= { count: 0, hash: 0, params: {} });
        const params = (to.params[key] ??= { pages: {} });
        const page = (params.pages[action.payload.page] ??= { items: [] });

        page.items = action.payload.items;
      },

      confirmationsClear: (
        store,
        action: PayloadAction<{
          accountNo: AccountKey;
          from: number;
          to: number;
        }>
      ) => {
        const confirmations = (store.confirmations[action.payload.accountNo] ??= { from: {} });
        const from = (confirmations.from[action.payload.from] ??= { to: {} });
        const to = (from.to[action.payload.to] ??= { count: 0, hash: 0, params: {} });

        to.count = 0;
        to.hash = 0;
        to.params = {};
      },

      profitLossItemSaved: (store, action: PayloadAction<CmcWebApi.Account.ProfitLossItemSaveRequest1['ProfitLossItem']>) => {
        const item = store.profitLoss[action.payload.AccountNumber]?.items?.find((i) => i.id === action.payload.Id);
        if (!item) return;

        if (action.payload.IsUserAveragePrice) {
          item.averagePrice = action.payload.AveragePrice;
          item.isUserAveragePrice = true;
        }
        if (action.payload.IsUserHoldingQuantity) {
          item.quantity = action.payload.HoldingQuantity;
          item.isUserHoldingQuantity = true;
        }
        item.cost = item.averagePrice * item.quantity;
      },

      cashBalanceFetched: (store, action: PayloadAction<CmcWebApi.IntermediaryCompany.CashAccountModel1>) => {
        if (!store.account[action.payload.AccountNumber]) return;
        store.account[action.payload.AccountNumber].data!.availableFunds = action.payload.Balance;
      }
    }
  });

export const { actions, reducer } = createAccountStore(defaultAccountStore);

export const createStore = (initialStore: IAccountStore = defaultAccountStore) =>
  configureStore({ reducer: { [ACCOUNT_STORE_NAME]: createAccountStore(initialStore).reducer } });

export const AccountStore = createStore();
export type IStore = ReturnType<typeof AccountStore.getState>;
